import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaLaptopCode, FaCloud, FaCogs, FaShieldAlt, FaRocket } from 'react-icons/fa';
import { theme } from '../styles/theme';

// Container
const ServicesContainer = styled.section`
  padding: 100px 0;
  background: ${theme.colors.primary};
  position: relative;
  overflow: hidden;
  
  &::before, &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${theme.colors.secondary}, transparent);
    opacity: 0.5;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const ServicesWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
  position: relative;
  z-index: 1;
`;

const ServicesHeading = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: 2.5rem;
  margin-bottom: 64px;
  text-align: center;
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: ${theme.typography.lineHeight.heading};
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: ${theme.colors.secondary};
    border-radius: ${theme.borderRadius.small};
  }
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const ServicesSubheading = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1.2rem;
  margin-bottom: 64px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${theme.typography.lineHeight.body};
  font-weight: ${theme.typography.fontWeight.regular};
`;

// Service Card
const ServiceCard = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 100px;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.large};
  background: rgba(255, 255, 255, 0.02);
  box-shadow: ${theme.shadows.card};
  transition: all 0.3s ease-in-out;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
    padding: ${theme.spacing.md};
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.large};
    border-color: rgba(212, 175, 55, 0.2);
  }
`;

const ServiceContent = styled.div`
  display: flex;
  flex-direction: ${({ $imgStart }) => ($imgStart ? 'row-reverse' : 'row')};
  align-items: center;
  gap: ${theme.spacing.xl};
  
  @media screen and (max-width: 768px) {
    flex-direction: column-reverse;
    gap: ${theme.spacing.lg};
  }
`;

const Column1 = styled.div`
  flex: 1;
  padding: 0 ${theme.spacing.sm};
  
  @media screen and (max-width: 768px) {
    margin-top: ${theme.spacing.lg};
    padding: 0;
  }
`;

const Column2 = styled.div`
  flex: 1;
  padding: 0 ${theme.spacing.sm};
  
  @media screen and (max-width: 768px) {
    margin-bottom: 0;
    padding: 0;
  }
`;

const ServiceImage = styled.div`
  width: 100%;
  height: 250px;
  background: ${theme.colors.gradient.primary};
  border-radius: ${theme.borderRadius.large};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.medium};
  border: 1px solid rgba(212, 175, 55, 0.3);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(212, 175, 55, 0.15) 0%, rgba(18, 22, 25, 0) 70%);
    z-index: 1;
  }
  
  svg {
    font-size: 120px;
    color: ${theme.colors.secondary};
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
    z-index: 2;
  }
  
  @media screen and (max-width: 768px) {
    height: 200px;
    
    svg {
      font-size: 100px;
    }
  }
  
  @media screen and (max-width: 480px) {
    height: 180px;
    
    svg {
      font-size: 80px;
    }
  }
`;

const ServiceTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.secondary};
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: ${theme.typography.lineHeight.heading};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: ${theme.colors.secondary};
    border-radius: ${theme.borderRadius.small};
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  line-height: ${theme.typography.lineHeight.body};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.fontWeight.regular};
`;

// Features List
const FeaturesList = styled.div`
  margin-top: ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: ${theme.spacing.xs} 0;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    transform: translateX(5px);
  }
`;

const FeatureIcon = styled.div`
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
  margin-right: ${theme.spacing.sm};
  margin-top: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  line-height: ${theme.typography.lineHeight.body};
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.fontWeight.regular};
`;

// Learn More Button
const LearnMoreBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 12px 28px;
  border-radius: ${theme.borderRadius.pill};
  font-size: 16px;
  font-weight: ${theme.typography.fontWeight.medium};
  text-decoration: none;
  margin-top: ${theme.spacing.lg};
  transition: all 0.3s ease-in-out;
  box-shadow: ${theme.shadows.small};
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${theme.shadows.medium};
    background: ${theme.colors.tertiary};
    color: ${theme.colors.text.primary};
  }
`;

// Enhanced animation component wrapper compatible with Framer Motion v10.16.4
const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ 
      duration: 0.7, 
      delay: delay || 0,
      ease: [0.6, 0.05, 0.01, 0.99] 
    }}
  >
    {children}
  </motion.div>
);

const Services = () => {
  return (
    <ServicesContainer id="services" aria-label="Our Services">
      <ServicesWrapper>
        <FadeIn>
          <ServicesHeading aria-label="Services heading">
            Our Services
          </ServicesHeading>
        </FadeIn>
        
        <FadeIn delay={0.2}>
          <ServicesSubheading>
            We build modern technology solutions tailored to your business needs. From web development to AI integration, we help African businesses transform and grow.
          </ServicesSubheading>
        </FadeIn>
        
        <FadeIn delay={0.3}>
          <ServiceCard>
            <ServiceContent $imgStart={true}>
              <Column1>
                <ServiceTitle>Web & Mobile Development</ServiceTitle>
                <ServiceDescription>
                  We build modern websites, business systems, customer portals, and mobile applications tailored to your business needs.
                </ServiceDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Custom website development</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Mobile app development (iOS & Android)</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Customer portals and dashboards</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>E-commerce solutions</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn 
                  href="#contact" 
                  aria-label="Learn more about Web & Mobile Development"
                >
                  Learn More
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <ServiceImage aria-hidden="true">
                  <FaLaptopCode />
                </ServiceImage>
              </Column2>
            </ServiceContent>
          </ServiceCard>
        </FadeIn>
        
        <FadeIn delay={0.4}>
          <ServiceCard>
            <ServiceContent $imgStart={false}>
              <Column1>
                <ServiceTitle>Artificial Intelligence</ServiceTitle>
                <ServiceDescription>
                  AI assistants, workflow automation, chatbots, document processing, and intelligent business solutions to streamline your operations.
                </ServiceDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>AI-powered chatbots and assistants</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Workflow automation</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Document processing and analysis</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Predictive analytics</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn 
                  href="#contact" 
                  aria-label="Learn more about Artificial Intelligence"
                >
                  Learn More
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <ServiceImage aria-hidden="true">
                  <FaCogs />
                </ServiceImage>
              </Column2>
            </ServiceContent>
          </ServiceCard>
        </FadeIn>
        
        <FadeIn delay={0.5}>
          <ServiceCard>
            <ServiceContent $imgStart={true}>
              <Column1>
                <ServiceTitle>Cloud Solutions</ServiceTitle>
                <ServiceDescription>
                  Microsoft Azure deployment, cloud migration, hosting, monitoring, backups, and infrastructure management for scalable business operations.
                </ServiceDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Cloud migration and deployment</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Infrastructure management</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Monitoring and backups</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Scalable hosting solutions</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn 
                  href="#contact" 
                  aria-label="Learn more about Cloud Solutions"
                >
                  Learn More
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <ServiceImage aria-hidden="true">
                  <FaCloud />
                </ServiceImage>
              </Column2>
            </ServiceContent>
          </ServiceCard>
        </FadeIn>
        
        <FadeIn delay={0.6}>
          <ServiceCard>
            <ServiceContent $imgStart={false}>
              <Column1>
                <ServiceTitle>Business Software</ServiceTitle>
                <ServiceDescription>
                  CRM systems, booking platforms, inventory management, HR systems, and custom enterprise applications for your business.
                </ServiceDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>CRM and customer management</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Booking and scheduling platforms</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Inventory management systems</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Custom enterprise applications</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn 
                  href="#contact" 
                  aria-label="Learn more about Business Software"
                >
                  Learn More
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <ServiceImage aria-hidden="true">
                  <FaRocket />
                </ServiceImage>
              </Column2>
            </ServiceContent>
          </ServiceCard>
        </FadeIn>

        <FadeIn delay={0.7}>
          <ServiceCard>
            <ServiceContent $imgStart={true}>
              <Column1>
                <ServiceTitle>Cybersecurity & IT</ServiceTitle>
                <ServiceDescription>
                  Security assessments, networking, CCTV integration, IT support, and managed services to protect your business.
                </ServiceDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Security assessments</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Network setup and management</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>CCTV integration</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>IT support and managed services</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn 
                  href="#contact" 
                  aria-label="Learn more about Cybersecurity & IT"
                >
                  Learn More
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <ServiceImage aria-hidden="true">
                  <FaShieldAlt />
                </ServiceImage>
              </Column2>
            </ServiceContent>
          </ServiceCard>
        </FadeIn>

        <FadeIn delay={0.8}>
          <ServiceCard>
            <ServiceContent $imgStart={false}>
              <Column1>
                <ServiceTitle>Digital Transformation</ServiceTitle>
                <ServiceDescription>
                  Helping businesses modernize operations through software, automation, and cloud technologies for competitive advantage.
                </ServiceDescription>
                <FeaturesList>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Business process automation</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Digital strategy consulting</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Legacy system modernization</FeatureText>
                  </FeatureItem>
                  <FeatureItem>
                    <FeatureIcon aria-hidden="true"><FaCheckCircle /></FeatureIcon>
                    <FeatureText>Technology adoption planning</FeatureText>
                  </FeatureItem>
                </FeaturesList>
                <LearnMoreBtn 
                  href="#contact" 
                  aria-label="Learn more about Digital Transformation"
                >
                  Learn More
                </LearnMoreBtn>
              </Column1>
              <Column2>
                <ServiceImage aria-hidden="true">
                  <FaRocket />
                </ServiceImage>
              </Column2>
            </ServiceContent>
          </ServiceCard>
        </FadeIn>
      </ServicesWrapper>
    </ServicesContainer>
  );
};

export default Services;
